import { Pipe, PipeTransform } from '@angular/core';

export type HighlightLanguage = 'ruby' | 'typescript' | 'curl' | 'json';

const KEYWORDS: Record<HighlightLanguage, string[]> = {
  ruby: ['require', 'puts'],
  typescript: ['import', 'from', 'const', 'await', 'new'],
  curl: ['curl'],
  json: [],
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// One regex, one pass, alternatives tried left to right. Doing it as
// separate replace() calls would let a later pass re-match the markup an
// earlier one inserted (class="code-str" is itself a quoted string) - the
// same trap docs-code.ts's highlightCode() documents.
function tokenizer(lang: HighlightLanguage): RegExp {
  const dq = String.raw`"(?:[^"\\\n]|\\.)*"`;
  // cURL wraps its whole JSON body in '...', so single quotes aren't
  // strings there; Ruby and TypeScript use them for real string literals.
  const sq = lang === 'ruby' || lang === 'typescript' ? String.raw`'(?:[^'\\\n]|\\.)*'` : '(?!)';
  const kw = KEYWORDS[lang].length ? String.raw`\b(?:${KEYWORDS[lang].join('|')})\b` : '(?!)';
  const flag = lang === 'curl' ? String.raw`(?<=\s)-[XHd](?=\s)` : '(?!)';
  return new RegExp(
    [
      String.raw`(${dq})(?=\s*:)`,                       // 1 "json_key":
      `(${dq})`,                                           // 2 "string"
      `(${sq})`,                                           // 3 'string'
      String.raw`((?<![:\w-])[A-Za-z_]\w*(?=:\s))`,        // 4 bare key: (Ruby / TS)
      String.raw`((?<![\w.])-?\d+(?:\.\d+)?\b)`,           // 5 number
      String.raw`(\b(?:true|false)\b)`,                    // 6 boolean
      String.raw`(\b(?:nil|null)\b)`,                      // 7 nil / null
      `(${kw}|${flag})`,                                   // 8 keyword / cURL flag
    ].join('|'),
    'g',
  );
}

const CLASS_BY_GROUP = ['', 'code-blue', 'code-str', 'code-str', 'code-blue', 'code-num', 'code-bool', 'code-op', 'code-keyword'];

/**
 * Syntax colouring for the Playground code panel: the request snippet
 * (Ruby / TypeScript / cURL) and the JSON response. Wraps tokens in the
 * .code-* spans from _shared-ui.scss.
 *
 * The input includes whatever the user typed into the form, so the text is
 * HTML-escaped first and the result is bound through [innerHTML] with
 * Angular's normal sanitizer (which keeps <span class>) - never
 * bypassSecurityTrustHtml.
 */
@Pipe({
  name: 'codeHighlight',
  standalone: false,
})
export class CodeHighlightPipe implements PipeTransform {
  transform(code: string | null | undefined, lang: HighlightLanguage): string {
    if (!code) return '';
    return escapeHtml(code).replace(tokenizer(lang), (match, ...groups: (string | undefined)[]) => {
      const i = groups.findIndex((g, idx) => idx < 8 && g !== undefined);
      return i === -1 ? match : `<span class="${CLASS_BY_GROUP[i + 1]}">${match}</span>`;
    });
  }
}
